import type { Recording } from 'core/http/api'
import { Folder } from 'core/http/api'

export enum AudioListErrors {
  None = 0,
  NoData = 'no-data',
  NoNetwork = 'no-network',
  ServerError = 'server-error'
}

export const useAudioFolders = defineStore('audio-folders', {
  state: () => {
    const loadAllData = ref(false)
    const context = useLazyAsyncData(
      'dataset:recording-folders',
      () => Folder.get(loadAllData.value ? { all: true } : undefined).catch((error) => {
        ErrorService.log(error, { title: 'Failed to fetch audio folders' })
        throw error
      }),
      {
        server: false,
        immediate: false,
        default: () => <Folder[]>[]
      }
    )

    const { status } = context
    const folders = ref<Folder[]>([])
    const folderId = ref<Folder.ID>(null)

    const fetching = computed(() => status.value === 'pending')
    const error = computed(() => {
      if (context.error.value instanceof Error) {
        return navigator.onLine ? AudioListErrors.ServerError : AudioListErrors.NoNetwork
      }

      return AudioListErrors.None
    })

    // Update base dataset
    watch(context.data, () => {
      folders.value = context.data.value ?? []
    })

    // Switch to default folder
    // if previous active folder doesn't exist after sync
    watchEffect(() => {
      const hasActiveFolder = !!folders.value.find(folder => folder.id === folderId.value)

      if (!hasActiveFolder)
        folderId.value = null
    })

    // Clear data on logout
    http.on('logout', () => context.clear())

    return {
      /** Async dataset context  */
      context,

      /** Whether data is being fetched */
      fetching,

      /** Active error of the db */
      error,

      /** Active folder's id */
      folderId,

      /** Whether to load full audio dataset */
      loadAllData,

      /** List of folders */
      folders
    }
  },

  getters: {
    /** All recordings in available folders */
    dataset(state): Recording[] {
      return state.folders.flatMap(i => i.recordings)
    },

    /** Current folder recordings */
    list(state): Recording[] {
      return state
        .folders
        .flatMap(i => i.recordings)
        .filter(e => e.folderId === state.folderId)
    }
  },

  actions: {
    /** Returns folder */
    get(id: Folder.ID) {
      return this.folders.find(folder => folder.id === id)
    },

    getStrict(id: Folder.ID) {
      const folder = this.get(id)

      if (!folder)
        throw new Error(`Could not find folder ${id}`)

      return folder
    },

    /** Creates audio folder */
    async create(name: string) {
      const folder = await Folder.create(name)
      this.folders.push(folder)
      return folder
    },

    rename(id: Folder.ID, name: string) {
      const folder = this.get(id)

      if (folder)
        folder.name = name

      return Folder.rename(id, name)
    },

    /** Remove folder */
    remove(id: Folder.ID) {
      const targetFolder = this.get(id)
      const defaultFolder = this.get(null)

      if (!targetFolder || !defaultFolder)
        throw new Error('targetFolder or defaultFolder is null')

      // Move all recordings to default folder
      defaultFolder.recordings.push(
        ...targetFolder.recordings.map((item) => {
          // Set recording folderId to null (default folder)
          item.folderId = null
          return item
        })
      )

      // Remove folder
      this.folders.splice(this.folders.indexOf(targetFolder), 1)

      // Set active folder to default
      this.folderId = null

      return Folder.remove(id)
    },

    /** Moves recording to target folder */
    moveItem(itemId: string, folderId: Folder.ID) {
      const item = this.getItemStrict(itemId)

      // Update local id
      item.folderId = folderId

      return Folder.moveItem(folderId, itemId)
    },

    /** Returns Recording item */
    getItem(id: string) {
      return this.dataset.find(item => item.id === id)
    },

    /** Throws error if recording item is not found */
    getItemStrict(id: string) {
      const item = this.getItem(id)

      if (!item)
        throw new Error(`Could not find recording ${id}`)

      return item
    },

    /** Edit recording data */
    editItem(id: string, data: Recording.EditableProps) {
      const item = this.getItemStrict(id)
      Object.assign(item, data)

      return api.audio.edit(item.id, data)
    },

    /** Deletes recording item */
    removeItem(id: string) {
      const item = this.getItemStrict(id)
      const folder = this.getStrict(item.folderId)
      const index = folder.recordings.indexOf(item)

      if (index < 0)
        throw new Error('Could not find recording item in folder')

      folder.recordings.splice(index, 1)

      return api.audio.remove(id)
    },

    async sync() {
      return this.context.refresh()
    },

    /** Whether to load all data */
    async setLoadAllData(value: boolean) {
      if (this.loadAllData === value)
        return

      this.loadAllData = value
      await this.sync()
    }
  }
})
